import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { History, ArrowRight, User, Filter } from "lucide-react";

const ACTOR_TONE = {
  admin: "bg-primary/10 text-primary border-primary/20",
  executive: "bg-cyan-50 text-cyan-700 border-cyan-200",
  public: "bg-zinc-100 text-zinc-600 border-zinc-200",
};

export const AuditList = ({ rows, showParcel = true }) => {
  if (!rows.length) {
    return <p className="py-8 text-sm text-muted-foreground text-center" data-testid="audit-empty">
      No activity recorded yet.
    </p>;
  }
  return (
    <div className="divide-y divide-border" data-testid="audit-list">
      {rows.map((r) => (
        <div key={r.id} className="py-4 flex flex-col sm:flex-row sm:items-start gap-3" data-testid={`audit-row-${r.id}`}>
          <span className={`shrink-0 self-start rounded-md border px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${ACTOR_TONE[r.actor_role] || ACTOR_TONE.public}`}>
            {r.actor_role}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm">
              <span className="font-semibold">{r.action_label}</span>
              {showParcel && r.parcel_name && (
                <>
                  {" · "}
                  <Link to={`/parcels/${r.parcel_id}`} className="text-primary hover:underline">
                    {r.parcel_name}
                  </Link>
                </>
              )}
            </p>
            <p className="mt-0.5 text-xs text-muted-foreground">{r.summary}</p>
            {r.changes?.length > 0 && (
              <div className="mt-2 space-y-1">
                {r.changes.slice(0, 8).map((c, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-2 text-xs">
                    <span className="font-medium">{c.label}</span>
                    {c.note ? (
                      <span className="text-muted-foreground">{c.note}</span>
                    ) : (
                      <>
                        <span className="font-mono px-1.5 py-0.5 rounded bg-muted text-muted-foreground line-through">
                          {c.from}
                        </span>
                        <ArrowRight size={11} className="text-muted-foreground" />
                        <span className="font-mono px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                          {c.to}
                        </span>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="sm:text-right shrink-0">
            <p className="text-xs font-semibold flex items-center gap-1.5 sm:justify-end">
              <User size={11} className="text-muted-foreground" /> {r.actor_name}
            </p>
            <p className="text-xs font-mono text-muted-foreground mt-0.5">{fmtDateTime(r.at)}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

export default function Activity() {
  const [data, setData] = useState({ total: 0, rows: [] });
  const [actions, setActions] = useState([]);
  const [action, setAction] = useState("all");
  const [days, setDays] = useState("30");
  const [limit, setLimit] = useState(100);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/audit/actions").then(({ data }) => setActions(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { limit };
    if (action !== "all") params.action = action;
    if (days !== "all") params.days = Number(days);
    api.get("/audit", { params })
      .then(({ data }) => setData(data))
      .catch(() => setData({ total: 0, rows: [] }))
      .finally(() => setLoading(false));
  }, [action, days, limit]);

  return (
    <div className="p-5 md:p-10 max-w-[1300px] rise" data-testid="activity-page">
      <p className="label-tiny">Governance</p>
      <h1 className="mt-2 font-display text-3xl md:text-4xl font-bold tracking-tighter flex items-center gap-3">
        <History size={28} className="text-primary" /> Audit trail
      </h1>
      <p className="mt-3 text-sm text-muted-foreground max-w-2xl">
        Every change to a property, document, case, mortgage, share link, team member or SLA
        setting is recorded with who did it, exactly what changed and when.
      </p>

      <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="audit-filters">
        <Filter size={15} className="text-muted-foreground" />
        <Select value={action} onValueChange={setAction}>
          <SelectTrigger className="w-[240px]" data-testid="audit-action-filter">
            <SelectValue placeholder="All activity" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All activity</SelectItem>
            {actions.map((a) => (
              <SelectItem key={a.action} value={a.action}>{a.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-[180px]" data-testid="audit-days-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="365">Last year</SelectItem>
            <SelectItem value="all">All time</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground" data-testid="audit-total">
          {loading ? "Loading…" : `${data.rows.length} of ${data.total} entries`}
        </span>
      </div>

      <div className="mt-6 surface p-6">
        <AuditList rows={data.rows} />
        {data.total > data.rows.length && (
          <div className="mt-5 text-center">
            <Button variant="outline" data-testid="audit-load-more"
              onClick={() => setLimit((l) => l + 100)}>Load more</Button>
          </div>
        )}
      </div>
    </div>
  );
}
